import { useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { selectGameConfig } from "src/store/common"
import { selectCurrentPlayer, selectIsCurrentPlayerReady, selectResearchLevels, selectResearchPoints } from "src/store/game"
import { researchPrice } from "src/utils/gameModifiers"

import "./ResearchPanel.css"

// What each research does, so the panel is not a list of names. Kept as copy in the translation
// files (`research.descriptions.<name>`) rather than on the server — the numbers all come from config.

/* Each side's lines, in the order the panel lists them. `GET /game-config/` publishes only the
   *capped* researches under `research_max_level` since S17 (the two keys) — every other line is
   endless — so the catalogue can no longer be read off that map. Anything the config does name
   that is missing here is appended, so a line added on the server still shows up (unnamed but
   buyable) before this list learns about it. Mirrors `simulator/domain/research.py`. */
const RESEARCHES = {
  human: [
    "attack",
    "defense",
    "health",
    "irrigation",
    "prosperity",
    "military_technology",
    "roads",
    "thermal_suits",
    "impenetrable",
    "missile_program",
  ],
  zombie: [
    "attack",
    "defense",
    "health",
    "rot_control",
    "outbreak",
    "scavenger",
    "microbiotic_connection",
    "stalker",
    "contagion",
    "tunnel_program",
  ],
}

/* The lines the server refuses (`ResearchRequiresProgram`) until the side's key is bought (S13).
   A courtesy only: the panel greys them and says why, the server is the gate.

   Empty since 2026-09-09, and the map stays because the mechanism does. Both special-building
   lines are now a key with their own ladder above it — the silo's reach since U6, the tunnel's
   network size since 2026-09-30 — so there is no line left that improves a building its owner
   may not have. */
const PROGRAM_FOR = {}

// Which lane each line of research belongs to. The tabs exist so a player looking for "why is my
// food so low" does not have to read past every combat upgrade to find the farm one.
const CATEGORIES = {
  attack: "combat",
  defense: "combat",
  health: "combat",
  impenetrable: "combat",
  contagion: "combat",
  missile_program: "combat",
  tunnel_program: "movement",
  irrigation: "economy",
  prosperity: "economy",
  military_technology: "economy",
  rot_control: "economy",
  outbreak: "economy",
  scavenger: "economy",
  roads: "movement",
  thermal_suits: "movement",
  microbiotic_connection: "movement",
  stalker: "movement",
}

const ICONS = {
  attack: "⚔",
  defense: "🛡",
  health: "✚",
  impenetrable: "🧱",
  contagion: "🦠",
  missile_program: "🚀",
  tunnel_program: "🕳",
  irrigation: "🌾",
  prosperity: "🏠",
  military_technology: "🔧",
  rot_control: "🥩",
  outbreak: "🥚",
  scavenger: "🦴",
  roads: "🛣",
  thermal_suits: "🧥",
  microbiotic_connection: "🕸",
  stalker: "👁",
}

const TABS = [
  { id: "all", labelKey: "research.tabs.all" },
  { id: "combat", labelKey: "research.tabs.combat" },
  { id: "economy", labelKey: "research.tabs.economy" },
  { id: "movement", labelKey: "research.tabs.movement" },
]

// A published rate as a percentage, for a description that quotes one. Rounded to whole points
// because every rate this panel words is one — a research that lands on 0.5% wants a decimal here
// rather than a number that reads as zero.
const percent = (rate) => `${Math.round((Number(rate) || 0) * 100)}%`

// Fallback for a research the dictionary does not name yet: "thermal_suits" → "Thermal suits".
const label = (name) => name.replace(/_/g, " ").replace(/^./, (character) => character.toUpperCase())

const ResearchPanel = ({ level }) => {
  const { t } = useTranslation()
  const gameConfig = useSelector(selectGameConfig)
  const player = useSelector(selectCurrentPlayer)
  const levels = useSelector(selectResearchLevels)
  const points = useSelector(selectResearchPoints)
  const isReady = useSelector(selectIsCurrentPlayerReady)
  const [tab, setTab] = useState("all")
  const [affordableOnly, setAffordableOnly] = useState(false)

  // A level-0 research building produces nothing, so offer no panel. The modal above already
  // says why in its warning banner; a second line here read as the same message twice.
  if (!level) return null

  const buy = (name) => wsClient.send("game", { action: "apply_research", research: name })

  const team = player?.team
  const rules = gameConfig?.rules_config || {}
  const maxLevels = gameConfig?.research_max_level || {}
  const fromConfig = Object.keys(maxLevels)
    .filter((key) => key.startsWith(`${team}:`))
    .map((key) => key.split(":")[1])
  // The server publishes the catalogue (`researches`, S17); the local list is only the fallback
  // for a payload from before it did.
  const names = [...new Set([...(gameConfig?.researches?.[team] || RESEARCHES[team] || []), ...fromConfig])]
  const catalogue = names.map((name) => {
    const key = `${team}:${name}`
    const level = levels[name] || 0
    // A research the config does not cap is endless (S17): the next level is always on offer, at
    // the ladder's price. Only the keys keep a ceiling, and the config says which those are.
    const maxLevel = maxLevels[key] ?? null
    const program = PROGRAM_FOR[name]
    return {
      name,
      level,
      maxLevel,
      // Every line doubles from its first price (2026-09-28; the programs open at 800 since
      // 2026-10-09), read off the published rules.
      cost: researchPrice({ rules, level, research: name }),
      maxed: maxLevel != null && level >= maxLevel,
      program,
      locked: Boolean(program) && (levels[program] || 0) < 1,
    }
  })

  const visible = catalogue.filter((research) => {
    if (tab !== "all" && CATEGORIES[research.name] !== tab) return false
    if (affordableOnly && (research.maxed || research.locked || points < research.cost)) return false
    return true
  })

  return (
    <div className="research-panel">
      <div className="research-filters">
        <div className="research-tabs" role="tablist">
          {TABS.map((option) => (
            <button
              key={option.id}
              role="tab"
              aria-selected={tab === option.id}
              className={`research-tab${tab === option.id ? " active" : ""}`}
              onClick={() => setTab(option.id)}
            >
              {t(option.labelKey)}
            </button>
          ))}
        </div>
        <button
          className={`research-affordable${affordableOnly ? " on" : ""}`}
          onClick={() => setAffordableOnly(!affordableOnly)}
        >
          {t("research.affordableOnly")}
        </button>
      </div>

      <div className="research-list">
        {visible.map((research) => {
          const affordable = points >= research.cost
          const programName = research.program
            ? t(`research.names.${research.program}`, { defaultValue: label(research.program) })
            : null
          return (
            <div
              className={`research-row${research.maxed ? " maxed" : ""}${research.locked ? " locked" : ""}`}
              key={research.name}
            >
              <span className={`research-icon research-icon-${CATEGORIES[research.name] || "combat"}`}>
                {ICONS[research.name] || "◆"}
              </span>

              <div className="research-name">
                <p className="research-title">
                  {t(`research.names.${research.name}`, { defaultValue: label(research.name) })}
                </p>
                <p className="research-effect">
                  {/* Each quantity is worded on its own, because a sentence carrying two of them
                      cannot pluralise both off i18next's single `count`. These read "3 celdas" and
                      "1 ronda" and are dropped into the description already written out. It was a
                      real sentence on screen, not a hypothetical: the reach bonus went 1 -> 3 on
                      2026-09-08 and the panel said "3 celda más de alcance" until this. */}
                  {t(`research.descriptions.${research.name}`, {
                    defaultValue: "",
                    range: t("research.units.cells", { count: rules.RESEARCH_RANGE_BONUS ?? 1 }),
                    // Contagion's two numbers, both the server's: what a level adds and where the
                    // line starts, since "+1%" alone answers neither "of what?" nor "from what?".
                    rise: percent(rules.RESEARCH_RAISE_BONUS),
                    riseBase: percent(rules.RAISE_CHANCE),
                    // What one level of every proportional line adds, read off the same constants
                    // `simulator/use_cases/modifiers.py` multiplies by.
                    stat: percent(rules.RESEARCH_STAT_BONUS),
                    production: percent(rules.RESEARCH_PRODUCTION_BONUS),
                    capacity: percent(rules.RESEARCH_CAPACITY_BONUS),
                    growth: percent(rules.RESEARCH_POPULATION_GROWTH_BONUS),
                    costCut: percent(rules.RESEARCH_COST_REDUCTION),
                    killFood: percent(rules.RESEARCH_KILL_FOOD_BONUS),
                    wall: percent(rules.RESEARCH_WALL_BONUS),
                    // Roads / Microbiotic connection: a share of the BASE speed (2026-09-29).
                    pace: percent(rules.ROADS_SPEED_BONUS),
                  })}
                </p>
                {research.locked && (
                  <p className="research-lock">🔒 {t("research.requiresProgram", { program: programName })}</p>
                )}
              </div>

              {/* One pip per level the research has, so how far along you are is a glance, not
                  arithmetic on "3/5". An endless line has no ceiling to draw pips against, so it
                  shows the level it has reached and nothing else. */}
              <span className="research-pips" aria-hidden="true">
                {Array.from({ length: research.maxLevel ?? 0 }).map((_, index) => (
                  <span
                    key={index}
                    className={`research-pip${index < research.level ? (research.maxed ? " maxed" : " on") : ""}`}
                  />
                ))}
              </span>

              <span className={`research-level${research.maxed ? " maxed" : ""}`}>
                {research.maxed
                  ? t("research.max")
                  : research.maxLevel == null
                    ? t("research.levelN", { level: research.level })
                    : `${research.level}/${research.maxLevel}`}
              </span>

              <button
                className={`research-buy${affordable && !research.maxed && !research.locked ? " affordable" : ""}`}
                disabled={isReady || research.maxed || research.locked || !affordable}
                title={research.locked ? t("research.requiresProgram", { program: programName }) : undefined}
                onClick={() => buy(research.name)}
              >
                {research.maxed ? "—" : research.cost}
              </button>
            </div>
          )
        })}
        {visible.length === 0 && <p className="research-empty">{t("research.nothingAffordable")}</p>}
      </div>
    </div>
  )
}

export default ResearchPanel
